<template>
    <div class="form-type-0">
        <form @submit.prevent="setSubscriptionType" @input="onInputChange" ref="form" novalidate>
            <CustomRadio
                v-model="subscriptionType"
                @update:modelValue="updateSubscription"
                inputKey="subscriptions"
                ref="subscriptions"
                :options="subscriptionsOptions"
                cssClass="custom-radio-type-4"
                :hideNonSelected="hideNonSelected"
                required
            />

            <button v-if="(!hideSaveButton || newSubscriptionSelected) && !hideNonSelected" type="submit" :disabled="loading || !newSubscriptionSelected">
                <span v-if="loading" class="loading-spinner-type-1"></span>
                <span v-else>{{ $t(btnLabel) }}</span>
            </button>
            <p v-if="formError" class="form-error">{{ $t('forms.subscription_select.error') }}</p>
            <p v-if="formSuccess" class="form-success">{{ $t('forms.subscription_select.success') }}</p>
        </form>
    </div>
</template>

<script>
    import { mapState, mapActions } from 'pinia';
    import { useOrderStore } from '@/stores/order';
    import { useAccountStore } from '@/stores/account';
    import { useProductsStore } from '@/stores/products';
    import CustomRadio from '@/components/elements/inputs/CustomRadio';
    import formMixin from '@/mixins/formMixin';
    import { timestampToDisplayDate } from '@/utils/dates';
    import { formatNumber } from '@/utils/formatting';

    export default {
        mixins: [formMixin],
        emits: ['saved'],
        props: {
            hideSaveButton: {
                type: Boolean,
                default: false
            },
            hideNonSelected: {
                type: Boolean,
                default: false
            },
            btnLabel: {
                type: String,
                default: 'forms.subscription_select.button'
            },
            forceIcon: {
                type: Boolean,
                default: false
            }
        },
        components: {
            CustomRadio
        },
        data() {
            return {
                subscriptionType: '',
                subscription: {}
            }
        },
        computed: {
            ...mapState(useProductsStore, ['availableProducts']),
            ...mapState(useOrderStore, ['newSubscriptionSelected', 'activeDiscount']),
            ...mapState(useOrderStore, {defaultSubscriptionType: 'subscriptionType'}),
            accountStore() {
                return useAccountStore();
            },
            subscriptionsOptions() {
                if (!this.availableProducts || !this.availableProducts.length) {
                    return [];
                }
                
                return this.availableProducts.map(product => {
                    const locale = this.$i18n.locale || 'en';
                    const titleKey = `title_${locale}`;
                    const descriptionKey = `description_${locale}`;
                    const featuresKey = `features_${locale}`;
                    
                    const discountedPrice = this.activeDiscount ? 
                        ((100 - parseFloat(this.activeDiscount)) * parseFloat(product.price) / 100).toFixed(2) : 
                        product.price;
                    
                    const bullets = (product[featuresKey] || []).map(text => ({
                        text
                    }));
                    
                    const noticeInfo = this.generateNoticeInfo({...product, price: discountedPrice});
                    
                    return {
                        value: product.product_main,
                        text: product[titleKey] || product.name,
                        description: product[descriptionKey] || '',
                        price: {
                            label: 'common.price_monthly_vat',
                            value: formatNumber(discountedPrice)
                        },
                        bullets,
                        ...(product.icon || this.forceIcon && { image: product.icon || 'cards' }),
                        ...(noticeInfo && { notice: noticeInfo })
                    };
                });
            }
        },
        methods: {
            ...mapActions(useOrderStore, ['updateSubscription', 'setSubscription', 'generateProduct']),
            formatNumber,
            setSubscriptionType() {
                const form = this.$refs.form;
                if(form.checkValidity()) {
                    this.setSubscription(this.subscriptionType);
                    
                    const product = this.generateProduct(this.subscriptionType);
                    const index = this.subscriptionsOptions.findIndex(subscriptionsOption => subscriptionsOption.value === this.subscriptionType);
                    
                    if(index !== -1) {
                        this.$emit('saved', {
                            product,
                            productType: this.subscriptionType,
                            price: this.subscriptionsOptions[index].price.value,
                            name: this.subscriptionsOptions[index].text
                        });
                    }
                } else {
                    this.validateAllFields();
                }
            },
            async currentSubscription() {
                return await this.accountStore.getSubscription();
            },
            generateNoticeInfo(product) {
                const productMain = product.product_main;
                if (this.subscription.product_main !== productMain && this.subscriptionType !== productMain) return null;

                const isSwitchingProduct = this.subscription.product_main !== this.subscriptionType;
                const isCurrentProduct = productMain === this.subscription.product_main;
                const isCurrentProductCancelledAlready = this.subscription.status === 'canceled';
                const isNewProduct = productMain === this.subscriptionType;

                const currentPrice = this.subscription?.price ? parseFloat(this.subscription.price) : 0;

                const newProduct = isNewProduct ? product : this.availableProducts.find(product => product.product_main === this.subscriptionType);
                const newSubscriptionPrice = newProduct?.price ? parseFloat(newProduct.price) : 0;

                const isUpgrading = newSubscriptionPrice > currentPrice;
                const isDowngrading = newSubscriptionPrice < currentPrice;

                const css = ((isCurrentProduct && isSwitchingProduct) ||
                    (isCurrentProduct && isCurrentProductCancelledAlready)) ? 'error' : '';

                let label = '';
                if(isSwitchingProduct) {
                    if(isNewProduct) {
                        if(isUpgrading) {
                            label = 'fields.options_subscriptions.notice.start_direct';
                        } else if(isDowngrading) {
                            label = 'fields.options_subscriptions.notice.start_after_current';
                        }
                    } else if (isCurrentProduct) {
                        if(isUpgrading) {
                            label = 'fields.options_subscriptions.notice.cancel_direct';
                        } else if(isDowngrading) {
                            label = 'fields.options_subscriptions.notice.duration_canceled';
                        }
                    }
                } else if (isCurrentProduct) {
                    if(isCurrentProductCancelledAlready) {
                        label = 'fields.options_subscriptions.notice.duration_canceled';
                    } else {
                        label = 'fields.options_subscriptions.notice.duration_renewal';
                    }
                }
                
                if (!label) {
                    return null;
                }
                
                return {
                    css,
                    label,
                    translationLabels: this.accountStore.account.paid_to_time ?
                        { renewalDate: timestampToDisplayDate(this.accountStore.account.paid_to_time || 0, false) } :
                        {}
                };
            },
            async setDefault() {
                this.subscriptionType = this.defaultSubscriptionType;

                this.subscription = await this.currentSubscription() || {
                    product_main: this.accountStore.productType
                };
            },
        },
        mounted() {
            useProductsStore().getProducts(); // This is a fallback and shouldn't be relied upon due to lack of a loading-state.
            this.setDefault();
        }
    }
</script>